import { Loader } from '@googlemaps/js-api-loader';
import { useEffect, useRef } from 'react';
import { FormInputAddress } from './FormInputAddress';
import { FormInputText } from './FormInputText';
import FormReactSelectResidenceInput from './FormReactSelectResidenceInput';

export function FormInputAddressComplete({
  municipalities,
  formData,
  isValid,
  errors,
  register,
  updateFormData,
  addressId,
  setShow = null,
  showManualAdress = false,
}) {
  const communesRef = useRef();

  communesRef.current = municipalities;

  useEffect(() => {
    if (['auto', 'autocarro', 'moto', 'ancillari_auto'].includes(formData.product)) {
      if (formData.address !== '' && formData.postal_code && !isValid('postal_code') && setShow !== null) {
        setShow(true);
      }
    } else if (formData.postal_code && !isValid('postal_code') && setShow !== null) {
      setShow(true);
    }
  }, [formData.postal_code]);

  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });

    loader.load().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        document.getElementById(addressId),
        {
          // request place_id so we can geocode it if street number is missing
          fields: ['formatted_address', 'name', 'address_components', 'place_id'],
          types: ['address'],
          componentRestrictions: { country: 'it' },
        },
      );
      const geocoder = new google.maps.Geocoder();

      const actionTakeOnAutocomplete = async () => {
        const place = autocomplete.getPlace();

        const clearInputs = {
          address: '',
          residence_commune_code: '',
          residence_province_code: '',
          postal_code: '',
        };

        if (place === undefined || place.address_components === undefined) {
          updateFormData(clearInputs);
          return;
        }

        const address = place.address_components.find((c) => c.types.includes('route'))?.long_name || '';
        const houseNumberFromPlace = place.address_components.find((c) => c.types.includes('street_number'))?.long_name;
        const municipalityName = place.address_components.find((c) => c.types.includes('administrative_area_level_3'))?.long_name;

        const commune = communesRef.current.find((m) => m.name === municipalityName);

        if (commune === undefined) {
          updateFormData(clearInputs);
          return;
        }

        const residence_commune_code = commune.cadastral_code;
        const residence_province_code = commune.communal_territory.car_plate_symbol;
        const postal_code = place.address_components.find((c) => c.types.includes('postal_code'))?.long_name ?? commune.postal_code;

        // If street_number wasn't in the place result, geocode the place_id as a fallback
        let houseNumber = houseNumberFromPlace;
        if (!houseNumber && place.place_id) {
          try {
            const geocodePromise = () => new Promise((resolve) => {
              geocoder.geocode({ placeId: place.place_id }, (results, status) => resolve({ results, status }));
            });
            // await geocode
            // eslint-disable-next-line no-await-in-loop
            const { results, status } = await geocodePromise();
            if (status === 'OK' && results?.[0]) {
              houseNumber = results[0].address_components.find((c) => c.types.includes('street_number'))?.long_name;
            }
          } catch (e) {
            // swallow geocode errors and continue without house number
          }
        }

        if ((postal_code === null || postal_code === '') && setShow !== null) {
          setShow(true);
        }

        updateFormData({
          address,
          house_number: houseNumber || '',
          residence_commune_code,
          residence_province_code,
          postal_code,
        });
      };

      autocomplete.addListener('place_changed', actionTakeOnAutocomplete);
      document.getElementById(addressId).onblur = actionTakeOnAutocomplete;
    });

    /* Deliberately no teardown. This used to run `loader.reset(); delete window.google;
       Loader.instance = undefined;` on unmount, which is unrecoverable: the Maps <script> stays
       in the DOM, so the next mount's loader.load() resolves from it immediately without ever
       re-defining window.google — measured, window.google is `undefined` from the first unmount
       onward for the life of the page. Any remount then died on `google.maps` being undefined.

       It went unnoticed while one screen mounted this once. Fideiussioni e Cauzioni mounts it
       for the contraente and again for the beneficiario, so the second one always crashed.
       The script is a page-level singleton and the loader is idempotent by design — leaving it
       loaded is the supported usage, and every per-mount Autocomplete is rebuilt below anyway. */
  }, []);

  return (
    <>
      <FormInputAddress
        valueAddress={formData.address}
        valueHouse={formData.house_number}
        label="Cerca l'indirizzo (comune se necessario)"
        subText="Se ci sono più strade con lo stesso nome, scrivi nell'indirizzo il comune"
        onAddressChange={(value) => updateFormData({ address: value })}
        onHouseNumberChange={(value) => updateFormData({ house_number: value })}
        addressError={errors.address?.message}
        houseNumberError={errors.house_number?.message}
        validAddress={isValid('address')}
        validHouseNumber={isValid('house_number')}
        addressId={addressId}
      />

      {formData.residence_commune_code !== ''
        && formData.residence_province_code !== ''
        && formData.postal_code !== ''
        && (
          <div>
            <p className="m-0 fw-bold" style={{ fontSize: '15px', marginTop: '-10px' }}>
              Comune:
              {' '}
              {municipalities.filter((opt) => opt.cadastral_code === formData.residence_commune_code).map((o) => ({ value: o.cadastral_code, label: `${o.name} (${o.communal_territory.car_plate_symbol})` }))[0]?.label}

            </p>
          </div>
        )}

      <div className="text-dark">
        <p
          className="mt-1 mb-3 fw-bold"
          style={{ fontSize: '13px', marginTop: '-10px' }}
        >
          oppure
          {' '}
          <button
            className="fw-bold text-decoration-underline border-0 text-success bg-transparent"
            onClick={() => setShow(true)}
          >
            inserisci l'indirizzo manualmente
          </button>
        </p>
      </div>
      {(showManualAdress !== false
        || errors.postal_code?.message
        || errors.residence_commune_code?.message
        || errors.residence_province_code?.message) && (
          <FormReactSelectResidenceInput
            label="Residenza (Dall'indirizzo)"
            options={municipalities}
            values={{
              residence_commune_code: formData.residence_commune_code,
              residence_province_code: formData.residence_province_code,
              postal_code: formData.postal_code,
            }}
            disabled={!(errors.residence_commune_code?.message || errors.residence_province_code?.message)}
            valid={isValid('residence_commune_code')}
            error={errors.residence_commune_code?.message || errors.residence_province_code?.message}
          >
            <FormInputText
              label="Codice Postale"
              registration={register('postal_code')}
              error={errors.postal_code?.message}
              valid={isValid('postal_code')}
            />
          </FormReactSelectResidenceInput>
      )}
    </>
  );
}
